import * as React from "react";
import {
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
  alpha,
  type ContainerProps,
  type PaperProps,
  type StackProps,
  type SxProps,
  type Theme,
} from "@mui/material";

type AuthPagePlacement = "center" | "aboveCenter" | "top";

type AuthPageProps = {
  children: React.ReactNode;
  maxWidth?: ContainerProps["maxWidth"];
  placement?: AuthPagePlacement;
  sx?: SxProps<Theme>;
};

export function AuthPage({
  children,
  maxWidth = "sm",
  placement = "aboveCenter",
  sx,
}: AuthPageProps) {
  const isTop = placement === "top";
  const isAboveCenter = placement === "aboveCenter";

  return (
    <Box
      sx={[
        (theme) => ({
          minHeight: "100dvh",
          bgcolor: "background.default",
          display: "flex",
          flexDirection: "column",
          backgroundImage: `radial-gradient(circle at top left, ${alpha(
            theme.palette.primary.main,
            0.1,
          )}, transparent 32rem)`,
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >

      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          width: "100%",
          alignItems: isTop ? "flex-start" : "center",
          justifyContent: "center",

          pt: isTop ? { xs: 5, sm: 7, md: 10 } : undefined,
          pb: isTop ? { xs: 4, sm: 6, md: 8 } : undefined,
          py: isTop ? undefined : { xs: 4, sm: 6, md: 8 },
        }}
      >
        <Container
          maxWidth={maxWidth}
          sx={{
            transform: isAboveCenter
              ? {
                xs: "none",
                sm: "translateY(-4vh)",
                md: "translateY(-8vh)",
              }
              : "none",
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
}

export function AuthBrand() {
  return (
    <Link
      href="/"
      underline="none"
      color="text.primary"
      aria-label="Logami Startseite"
      sx={{
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          mb: 2,
        }}
      >
        <Box
          component="img"
          src="/logami.svg"
          alt=""
          aria-hidden="true"
          sx={{
            width: 28,
            height: 28,
            display: "block",
          }}
        />

        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.3,
          }}
        >
          Logami
        </Typography>
      </Stack>
    </Link>
  );
}

type AuthCardProps = PaperProps & {
  children: React.ReactNode;
};

export function AuthCard({ children, sx, ...props }: AuthCardProps) {
  return (
    <Paper
      elevation={0}
      {...props}
      sx={[
        {
          width: "100%",
          p: { xs: 3, sm: 4 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          backgroundImage: "none",
          bgcolor: "background.paper",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Paper>
  );
}

type AuthHeaderProps = {
  title: string;
  subtitle?: React.ReactNode;
  eyebrow?: React.ReactNode;
  align?: "left" | "center";
};

export function AuthHeader({
  title,
  subtitle,
  eyebrow,
  align = "left",
}: AuthHeaderProps) {
  return (
    <Stack spacing={1} alignItems={align === "center" ? "center" : "flex-start"}>
      {eyebrow ? (
        <Typography
          variant="overline"
          color="primary"
          fontWeight={700}
          sx={{ letterSpacing: "0.08em" }}
        >
          {eyebrow}
        </Typography>
      ) : null}

      <Typography
        variant="h4"
        component="h1"
        fontWeight={700}
        textAlign={align}
      >
        {title}
      </Typography>

      {subtitle ? (
        <Typography variant="body1" color="text.secondary" textAlign={align}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
  );
}

type AuthInfoPanelProps = {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
};

export function AuthInfoPanel({ children, sx }: AuthInfoPanelProps) {
  return (
    <Paper
      variant="outlined"
      sx={[
        {
          p: 2,
          borderRadius: 2,
          bgcolor: "background.default",
          backgroundImage: "none",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack spacing={0.75}>{children}</Stack>
    </Paper>
  );
}

type AuthActionsProps = StackProps & {
  children: React.ReactNode;
  align?: "left" | "right" | "between" | "center";
};

export function AuthActions({
  children,
  align = "right",
  sx,
  ...props
}: AuthActionsProps) {
  const justifyContent =
    align === "between"
      ? "space-between"
      : align === "center"
        ? "center"
        : align === "left"
          ? "flex-start"
          : "flex-end";

  return (
    <Stack
      direction={{ xs: "column-reverse", sm: "row" }}
      spacing={2}
      justifyContent={justifyContent}
      sx={[
        {
          pt: 1,
          "& > .MuiButton-root": {
            minHeight: 44,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...props}
    >
      {children}
    </Stack>
  );
}

type AuthContentProps = {
  children: React.ReactNode;
  spacing?: number;
};

export function AuthContent({ children, spacing = 3 }: AuthContentProps) {
  return <Stack spacing={spacing}>{children}</Stack>;
}
